/* Pure helpers for the admin camp review (CampReviewPage) and the Past camps table.
   Every time is formatted in the camp's own timezone, never the viewer's; an unknown IANA name
   falls back to the backend's own default zone. */

export const FALLBACK_TZ = 'America/Chicago';

// en-US time formats use U+202F before AM/PM in current ICU; plain spaces read and copy better.
const clean = (s) => s.replace(/[  ]/g, ' ');

const fmt = (iso, tz, opts) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return clean(new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts }).format(d));
  } catch {
    return clean(new Intl.DateTimeFormat('en-US', { timeZone: FALLBACK_TZ, ...opts }).format(d));
  }
};

export const fmtTime = (iso, tz) => fmt(iso, tz, { hour: 'numeric', minute: '2-digit' });
// `3:00–5:00 PM`: the start drops its AM/PM when the end carries the same one.
export const fmtRange = (startIso, endIso, tz) => {
  const a = fmtTime(startIso, tz);
  const b = fmtTime(endIso, tz);
  if (b === '—') return a;
  if (a === '—') return b;
  const m = / (AM|PM)$/.exec(a);
  return `${m && b.endsWith(m[0]) ? a.slice(0, -m[0].length) : a}–${b}`;
};
export const fmtHour = (ms, tz) => fmt(new Date(ms).toISOString(), tz, { hour: 'numeric' });
const fmtDayHour = (ms, tz) => fmt(new Date(ms).toISOString(), tz, { month: 'short', day: 'numeric', hour: 'numeric' });
const localHour = (ms, tz) => Number(fmt(new Date(ms).toISOString(), tz, { hour: 'numeric', hourCycle: 'h23' })) % 24;
export const localDay = (ms, tz) => fmt(new Date(ms).toISOString(), tz, { year: 'numeric', month: '2-digit', day: '2-digit' });
export const fmtDate = (iso, tz) => fmt(iso, tz, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
// Date tile parts: `OCT` / `24`.
export const fmtMonth = (iso, tz) => fmt(iso, tz, { month: 'short' }).toUpperCase();
export const fmtDay = (iso, tz) => fmt(iso, tz, { day: 'numeric' });

export const tzAbbr = (iso, tz) => {
  if (!iso) return '';
  const abbr = (zone) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' }).formatToParts(new Date(iso));
    return (parts.find((p) => p.type === 'timeZoneName') || {}).value || '';
  };
  try {
    return abbr(tz);
  } catch {
    try { return abbr(FALLBACK_TZ); } catch { return ''; }
  }
};

export const HOUR = 3600000;
const TICK_STEPS_H = [1, 2, 3, 4, 6, 12, 24];
const MAX_TICKS = 12;

/* Axis ticks for [lo, hi] (hour-aligned ms): the smallest step from TICK_STEPS_H giving at
   most MAX_TICKS labels (whole days beyond that), placed on camp-local multiples of the step.
   When the span crosses local midnight, the first tick of each day carries its date. */
export function axisTicks(lo, hi, tz, crossesDay) {
  const spanH = (hi - lo) / HOUR;
  const stepH = TICK_STEPS_H.find((h) => Math.floor(spanH / h) + 1 <= MAX_TICKS)
    || 24 * Math.ceil(spanH / 24 / (MAX_TICKS - 1));
  const align = Math.min(stepH, 24);
  let t = lo;
  for (let i = 0; i < align && localHour(t, tz) % align !== 0; i += 1) t += HOUR;
  const ticks = [];
  let prevDay = null;
  for (; t <= hi && ticks.length < MAX_TICKS; t += stepH * HOUR) {
    const day = localDay(t, tz);
    const label = crossesDay && day !== prevDay ? fmtDayHour(t, tz) : fmtHour(t, tz);
    ticks.push({ t, label });
    prevDay = day;
  }
  return ticks;
}

export const mins = (m) => (m === null || m === undefined ? '—' : `${Math.round(m)} min`);
export const secs = (s) => {
  if (s === null || s === undefined) return '—';
  const r = Math.round(s);
  return r < 90 ? `~${r} s` : `~${Math.floor(r / 60)} m ${r % 60} s`;
};
export const toMs = (iso) => (iso ? new Date(iso).getTime() : NaN);

// Hover title for a timeline bar or station strip: the lane label, then one line per save.
export const saveTooltip = (row, names, tz) => [
  row.label,
  ...(row.ticks || []).map((t) => `${names[t.athlete_id] || `Athlete #${t.athlete_id}`} ${fmtTime(t.at, tz)}`),
].join('\n');

/* Timeline lane group. The category comes from the sport catalog
   (sport_measurables.category_label, sent on every stats[]/stations[] row), never from a
   frontend stat list: Size → Measurables, anything else → Drills. */
export const laneGroup = (row) => {
  if (row.stat === 'check_in' || row.stat === 'photos') return 'arrival';
  return row.category === 'Size' ? 'measurables' : 'drills';
};

export const FLAG_KIND_LABEL = {
  dual_unmatched: 'Two-person entry',
  dual_mismatch: 'Two-person entry',
  checked_in_no_stats: 'Coverage',
  missing_stat: 'Coverage',
  schedule_offset: 'Schedule',
  quiet_gap: 'Timing',
  station_quiet: 'Timing',
  out_of_range: 'Value check',
  attempt_jump: 'Value check',
  history_jump: 'History',
  sync_burst: 'Sync',
  other_day_rows: 'Sync',
  population_outlier: 'Population',
  no_comparison_group: 'Population',
  test_accounts_ignored: 'Population',
};

/* How far the first save was from the scheduled start. Within `tolerance` minutes is on time;
   more than an hour either way is red, anything else amber. */
export const offsetLabel = (minutes, tolerance) => {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return { text: '—', tone: 'muted' };
  if (Math.abs(minutes) <= tolerance) return { text: 'on time', tone: 'green' };
  const n = Math.round(Math.abs(minutes));
  return { text: `${n} min ${minutes < 0 ? 'early' : 'late'}`, tone: n > 60 ? 'red' : 'amber' };
};
